import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  EventMessage,
  EventType,
  InteractionStatus,
  AuthenticationResult,
} from '@azure/msal-browser';
import { Subject, filter, takeUntil } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from 'app/modules/authentication/services/msa-auth.service';
import { AppDataService } from 'app/modules/authentication/services/appdata.service';
import { Constant } from 'app/core/common/constant';
import * as branch from 'branch-sdk';

declare let gtag: Function; // Declare gtag
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'BIZPAY';
  private readonly _destroying$ = new Subject<void>();
  msalInitialized: boolean = false;

  private readonly destroy$ = new Subject<void>();
  userProfile: any | null = null;

  constructor(
    private router: Router,
    private msalBroadcastService: MsalBroadcastService,
    private msalService: MsalService,
    private auth: AuthService,
    private appDataService: AppDataService
  ) {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        if (event.urlAfterRedirects === '/user/onboard') {
          gtag('event', 'conversion_event_signup_1', {
            event_category: 'User Onboarding',
            event_label: 'Signup Flow',
            value: 1,
          });
        }
      });
  }

  // ngOnInit(): void {
  //   // --- STEP 1: Handle MSAL Redirects and Initialization ---
  //   // This is the MOST IMPORTANT part. It processes the URL after a redirect from Azure AD.
  //   // It also initializes the core MSAL PublicClientApplication instance.
  //   // ALWAYS call this first in your root component's ngOnInit.
  //   this.msalService.instance
  //     .handleRedirectPromise()
  //     .then((authResult) => {
  //       // MSAL's PublicClientApplication is now initialized.
  //       this.msalInitialized = true;

  //       if (authResult && authResult.account) {
  //         // If a login redirect happened and an account was found, set it as active.
  //         console.log('MSAL Redirect successful. Account:', authResult.account);
  //         this.msalService.instance.setActiveAccount(authResult.account);
  //       } else {
  //         // If no redirect happened, or no account from redirect,
  //         // check for existing accounts in MSAL's cache.
  //         const accounts = this.msalService.instance.getAllAccounts();
  //         if (accounts.length > 0) {
  //           this.msalService.instance.setActiveAccount(accounts[0]);
  //         }
  //       }
  //       // Update our component's login status after MSAL has processed its state.
  //       this.updateLoginStatus();
  //     })
  //     .catch((error) => {
  //       // Handle any errors that occur during the redirect processing.
  //       console.error('MSAL handleRedirectPromise error:', error);
  //       // Mark as initialized even on error to prevent blocking the UI,
  //       // but ensure your UI reflects the error state.
  //       this.msalInitialized = true;
  //       this.updateLoginStatus(false);
  //     });

  //   // --- STEP 2: Subscribe to MSAL Login/Logout Events ---
  //   // React to authentication successes and failures.
  //   this.msalBroadcastService.msalSubject$ // Using msalSubject$ as per your code
  //     .pipe(
  //       filter(
  //         (msg: EventMessage) =>
  //           msg.eventType === EventType.LOGIN_SUCCESS ||
  //           msg.eventType === EventType.LOGIN_FAILURE ||
  //           msg.eventType === EventType.LOGOUT_SUCCESS // Listen for logout as well
  //       ),
  //       takeUntil(this._destroying$) // Unsubscribe on component destruction
  //     )
  //     .subscribe((result: EventMessage) => {
  //       console.log('MSAL Event:', result.eventType, result.payload);
  //       if (result.eventType === EventType.LOGIN_SUCCESS) {
  //         // On successful login, ensure the account is active.
  //         const payload = result.payload as AuthenticationResult;
  //         if (payload.account) {
  //           this.msalService.instance.setActiveAccount(payload.account);
  //         }
  //       } else if (result.eventType === EventType.LOGOUT_SUCCESS) {
  //         // On successful logout, clear the active account.
  //         this.msalService.instance.setActiveAccount(null);
  //       }
  //       // Update login status whenever a relevant event occurs.
  //       this.updateLoginStatus();
  //     });
  // }

  ngOnInit(): void {
    this.initializeBranch();
    // 1) Respond to MSAL events (login, logout, token failures)
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.LOGOUT_SUCCESS ||
            msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((msg: EventMessage) => {
        if (msg.eventType === EventType.LOGIN_SUCCESS) {
          const payload = msg.payload as AuthenticationResult;
          if (payload.account) {
            this.msalService.instance.setActiveAccount(payload.account);
          }
          // After a successful login, load user + org
          this.loadUserAndOrgThenNavigate();
        }

        if (msg.eventType === EventType.LOGOUT_SUCCESS) {
          this.msalService.instance.setActiveAccount(null);
          this.userProfile = null;
          // Optionally send to public landing
          // this.router.navigate(['/login']);
        }

        if (msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
          // Optional: show toast or trigger interactive if needed
          // e.g., Conditional Access / consent required
          // this.toastr.error('Authentication needed to continue');
        }
      });

    // 2) Wait until MSAL is idle on app start/refresh, then decide what to do
    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        // Ensure active account is set (post-refresh)
        const active =
          this.msalService.instance.getActiveAccount() ||
          this.msalService.instance.getAllAccounts()[0] ||
          null;

        // Only proceed with MSAL flow if user is actually an SSO user.
        // Skip if user logged in via email/password (avoids stale MSAL cache interference).
        const authMode = localStorage.getItem('authMode');
        const hasToken = !!localStorage.getItem(Constant.LocalStorageKey.Token);

        if (active && (!hasToken || authMode === 'msal')) {
          this.msalService.instance.setActiveAccount(active);
          // When already signed in (refresh), load user + org
          this.loadUserAndOrgThenNavigate();
        } else {
          // Not signed in or non-SSO user: let guards handle routing
        }
      });
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();

    this.destroy$.next();
    this.destroy$.complete();
  }

  updateLoginStatus(navigate = true) {
    if (navigate) {
      this.router.navigate(['/msa-active']);
    }
  }

  private loadUserAndOrgThenNavigate(): void {
    // Use window.location instead of router.url — on refresh, Angular's router
    // hasn't resolved the URL yet (still '/'), so router.url would cause the
    // saved URL to be lost and fall back to the role dashboard.
    const currentUrl = window.location.pathname + window.location.search;
    const publicPaths = ['/', '/login', '/sign-up', '/msa-active', '/sso-data-verification'];
    const isProtectedRoute = !publicPaths.some(p => currentUrl === p || currentUrl.startsWith(p + '?'));

    if (isProtectedRoute) {
      this.appDataService.navigateUrl = currentUrl;
    }

    // This uses your AuthService.getGraphTokenObservable + loadProfileAndOrg
    this.auth.loadProfileAndOrg().subscribe((data) => {
      if (data) {
        this.userProfile = data; // keep in state or push to a store/service
        // Navigate to your post-login area only if not already there
        if (this.router.url !== '/msa-active') {
          this.router.navigate(['/msa-active']);
        }
      } else {
        // If org scope is missing admin consent, data may be null.
        // Optionally: try elevate scope interactively before calling org
        // this.requestOrgScopeThenReload();
      }
    });
  }

  initializeBranch() {
    // key_live_jEfCOP0t7dwyVNtXJ56KeaadDCbckxNB branch key
    branch.init(environment.branchKey, (err, data) => {
      if (err) {
        console.error('Branch init error:', err);
        return;
      }
      console.log('Branch init session data:', data);
      console.log(data['data_parsed']['+clicked_branch_link']);
      
      // Check if the user was referred from a Branch link
      if (data && data['data_parsed']['+clicked_branch_link']) {

        // data_parsed contains custom deep link data
        const deepLinkData = data['data_parsed'];
        
        // Extracting the targeted URL from the '~feature' key
        let targetUrl = deepLinkData['~feature'];

        if (targetUrl){
          console.log('Deep Link Extracted:', targetUrl);

          // This regex replaces // (after the protocol) with /
          const cleanedUrl = targetUrl.replace(/([^:]\/)\/+/g, "$1");

          window.location.href = cleanedUrl;
        }
      }
    });
  }
}
